import type { ChatTaskContext } from "../types";
import {
  TASK_OUTPUT_DIALOG_OPENER_PAYLOAD_PROPERTY,
  TASK_OUTPUT_DIALOG_SESSION_QUERY_PARAM,
  createTaskOutputDialogSessionId,
  isTaskOutputDialogResultEnvelope,
  normalizeTaskOutputDialogSelection,
  taskOutputDialogPayloadKey,
  type TaskOutputDialogInitialPayload,
} from "../taskOutputDialog/taskOutputDialogProtocol";
import {
  applyDialogPopupWindowGeometry,
  buildCenteredDialogPopupWindow,
  focusDialogPopupWindow,
} from "./dialogPopupWindow";

const DIALOG_WINDOW_NAME_PREFIX = "finster_task_output_dialog_";
const CLOSE_WATCHER_INTERVAL_MS = 250;
const CLOSED_RESULT_GRACE_MS = 50;

type TaskOutputDialogParentWindow = Window & {
  __finsterTaskOutputDialogPayload__?: unknown;
};

interface ActiveTaskOutputDialogSession {
  popup: Window;
  cancel: () => void;
}

let activeSession: ActiveTaskOutputDialogSession | null = null;

function clearStagedPayload(sessionId: string): void {
  try {
    localStorage.removeItem(taskOutputDialogPayloadKey(sessionId));
  } catch {
    // The opener fallback is cleared independently.
  }
  try {
    const parentWindow = window as TaskOutputDialogParentWindow;
    const staged = parentWindow[TASK_OUTPUT_DIALOG_OPENER_PAYLOAD_PROPERTY];
    if (
      staged &&
      typeof staged === "object" &&
      (staged as { sessionId?: unknown }).sessionId === sessionId
    ) {
      delete parentWindow[TASK_OUTPUT_DIALOG_OPENER_PAYLOAD_PROPERTY];
    }
  } catch {
    // The popup consumes storage eagerly, so a stale opener fallback is non-critical.
  }
}

function stagePayload(payload: TaskOutputDialogInitialPayload): void {
  try {
    localStorage.setItem(taskOutputDialogPayloadKey(payload.sessionId), JSON.stringify(payload));
  } catch {
    // Some Office hosts partition or disable storage; use the opener fallback.
  }
  try {
    (window as TaskOutputDialogParentWindow)[TASK_OUTPUT_DIALOG_OPENER_PAYLOAD_PROPERTY] = payload;
  } catch {
    // The storage channel remains available in normal same-origin browsers.
  }
}

function buildTaskOutputDialogUrl(sessionId: string): string {
  const url = new URL("/dialogs/task-output.html", window.location.origin);
  url.searchParams.set(TASK_OUTPUT_DIALOG_SESSION_QUERY_PARAM, sessionId);
  return url.toString();
}

function replaceActiveSession(): void {
  activeSession?.cancel();
}

export interface OpenTaskOutputDialogOptions {
  userEmail: string;
}

/**
 * Opens the task-output picker in a same-origin popup.
 *
 * Only the user email and session nonce are staged for first paint. The
 * selected output and its citations return through postMessage so large task
 * results never touch URL parameters or localStorage.
 */
export function openTaskOutputDialog({
  userEmail,
}: OpenTaskOutputDialogOptions): Promise<ChatTaskContext | null> {
  replaceActiveSession();

  const sessionId = createTaskOutputDialogSessionId();
  const payload: TaskOutputDialogInitialPayload = { sessionId, userEmail };
  stagePayload(payload);
  const popupGeometry = buildCenteredDialogPopupWindow();

  let popup: Window | null = null;
  try {
    popup = window.open(
      buildTaskOutputDialogUrl(sessionId),
      `${DIALOG_WINDOW_NAME_PREFIX}${sessionId}`,
      popupGeometry.features
    );
  } catch {
    clearStagedPayload(sessionId);
    return Promise.resolve(null);
  }

  if (!popup) {
    clearStagedPayload(sessionId);
    return Promise.resolve(null);
  }

  const openedPopup = popup;
  applyDialogPopupWindowGeometry(openedPopup, popupGeometry);
  focusDialogPopupWindow(openedPopup);

  return new Promise<ChatTaskContext | null>((resolve) => {
    let settled = false;
    let closeWatcher: number | null = null;
    let closeGraceTimer: number | null = null;

    const teardown = () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("pagehide", onPageHide);
      if (closeWatcher !== null) {
        window.clearInterval(closeWatcher);
        closeWatcher = null;
      }
      if (closeGraceTimer !== null) {
        window.clearTimeout(closeGraceTimer);
        closeGraceTimer = null;
      }
      clearStagedPayload(sessionId);
      if (activeSession?.popup === openedPopup) {
        activeSession = null;
      }
      try {
        if (!openedPopup.closed) openedPopup.close();
      } catch {
        // The popup may already be gone or controlled by a restrictive host.
      }
    };

    const settle = (selection: ChatTaskContext | null) => {
      if (settled) return;
      settled = true;
      teardown();
      resolve(selection);
    };

    function onMessage(event: MessageEvent): void {
      if (event.source !== openedPopup) return;
      if (event.origin !== window.location.origin) return;
      if (!isTaskOutputDialogResultEnvelope(event.data)) return;
      if (event.data.sessionId !== sessionId) return;

      const selection = event.data.payload.selection;
      settle(selection === null ? null : normalizeTaskOutputDialogSelection(selection));
    }

    function onPageHide(): void {
      settle(null);
    }

    activeSession = {
      popup: openedPopup,
      cancel: () => settle(null),
    };

    window.addEventListener("message", onMessage);
    window.addEventListener("pagehide", onPageHide);
    closeWatcher = window.setInterval(() => {
      if (!openedPopup.closed || settled || closeGraceTimer !== null) return;
      if (closeWatcher !== null) {
        window.clearInterval(closeWatcher);
        closeWatcher = null;
      }
      // A queued postMessage can arrive immediately after `closed` flips. Give
      // that result one event-loop turn before treating window-chrome close as cancel.
      closeGraceTimer = window.setTimeout(() => settle(null), CLOSED_RESULT_GRACE_MS);
    }, CLOSE_WATCHER_INTERVAL_MS);
  });
}

export function resetTaskOutputDialogStateForTests(): void {
  replaceActiveSession();
  activeSession = null;
  try {
    delete (window as TaskOutputDialogParentWindow)[TASK_OUTPUT_DIALOG_OPENER_PAYLOAD_PROPERTY];
  } catch {
    // Test cleanup is best-effort.
  }
}
